import {
  CreateMedicineItemPayload,
  MedicineItemType,
  MedicineStatus,
  MedicineTimingRuleType,
  PendingChangeType,
  PillboxItem,
  PillboxPendingChange,
} from "@/types/pillbox";

export interface PendingGroup {
  key: string;
  itemType: MedicineItemType;
  name: string;
  items: PillboxPendingChange[];
}

// Groups ADD pending changes by case-insensitive (itemType, name) — the
// same medicine mentioned across multiple prescriptions collapses into one
// review card ("In N prescriptions") instead of one per prescription.
// DOSE_CHANGE/DISCONTINUE changes aren't part of this review flow yet.
export function groupPendingChanges(
  pending: PillboxPendingChange[]
): PendingGroup[] {
  const map = new Map<string, PendingGroup>();
  for (const item of pending) {
    if (item.changeType !== PendingChangeType.ADD) continue;
    const fields = item.proposedFields as Partial<CreateMedicineItemPayload>;
    const name = (fields.name || "").trim();
    const key = `${item.itemType}::${name.toLowerCase()}`;
    if (!map.has(key)) {
      map.set(key, { key, itemType: item.itemType, name, items: [] });
    }
    map.get(key)!.items.push(item);
  }
  return Array.from(map.values());
}

// Synthesizes a PillboxItem-shaped draft from a pending change's
// proposedFields, so the same AddMedicine.tsx prefill logic used for
// edit/restore modes can be reused for the review-medication screen.
// Start date defaults to the prescription's date, not today — R2/Flow1/3.
export function mapPendingChangeToDraftItem(
  pending: PillboxPendingChange
): PillboxItem {
  const fields = pending.proposedFields as Partial<CreateMedicineItemPayload>;
  const timingRuleType =
    fields.timingRuleType ??
    (fields.slots?.length || fields.dayFrequency
      ? MedicineTimingRuleType.FIXED_SLOTS
      : MedicineTimingRuleType.CONDITIONAL);

  return {
    id: pending.id,
    itemType: pending.itemType,
    memberId: pending.memberId,
    name: fields.name || "",
    dose: fields.dose,
    frequency: fields.frequency,
    dayFrequency: fields.dayFrequency,
    timingRuleType,
    slots: fields.slots,
    recurrenceIntervalDays: fields.recurrenceIntervalDays,
    selectedWeekdays: fields.selectedWeekdays,
    route: fields.route,
    notes: fields.notes,
    reasonForTaking: fields.reasonForTaking,
    foodContext: fields.foodContext,
    doctorName: pending.doctorName,
    dateOfVisit: pending.dateOfVisit,
    startDate: fields.startDate || pending.dateOfVisit || "",
    endDate: fields.endDate,
    status: MedicineStatus.ACTIVE,
    needsRefill: false,
    hasOverlap: false,
  };
}
